import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Modal from '../Common/Modal';
import APIServiceConfig from '../Users/APIServiceConfig';

const FilterDropdown = ({ isOpen, onClose, filters, setFilters, onApplyFilters, buttonRef }) => {
    const dropdownRef = useRef(null);
    const [dropdownStyle, setDropdownStyle] = useState(null);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };

        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                buttonRef.current && !buttonRef.current.contains(e.target)) {
                onClose();
            }
        };

        const positionDropdown = () => {
            if (!isOpen || !buttonRef.current || !dropdownRef.current) return;
            const buttonRect = buttonRef.current.getBoundingClientRect();

            // Simple positioning logic similar to InvoiceCreateFilters
            setDropdownStyle({
                position: 'absolute',
                top: buttonRect.bottom + window.scrollY + 5,
                left: buttonRect.left + window.scrollX,
                zIndex: 50
            });
        };

        if (isOpen) {
            window.addEventListener('keydown', handleEscape);
            document.addEventListener('mousedown', handleClickOutside);
            positionDropdown();
            window.addEventListener('resize', positionDropdown);
            window.addEventListener('scroll', positionDropdown, true);
        }

        return () => {
            window.removeEventListener('keydown', handleEscape);
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('resize', positionDropdown);
            window.removeEventListener('scroll', positionDropdown, true);
        };
    }, [isOpen, onClose, buttonRef]);

    if (!isOpen) return null;

    return (
        <div
            ref={dropdownRef}
            className="bg-white rounded-lg shadow-lg border border-gray-200 w-80"
            style={dropdownStyle}
        >
            <div className="p-4">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="space-y-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Service Status</label>
                        <select
                            value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        >
                            <option value="">All</option>
                            <option value="enabled">Enabled</option>
                            <option value="disabled">Disabled</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Client Name</label>
                        <input
                            type="text"
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            placeholder="Search client..."
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                    </div>
                </div>

                <div className="mt-4 flex justify-end space-x-2">
                    <button
                        onClick={onClose}
                        className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onApplyFilters}
                        className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                    >
                        Apply
                    </button>
                </div>
            </div>
        </div>
    );
};

const AdminAPIServiceClients = ({ service, onBack }) => {
    const [clients, setClients] = useState([]);
    const [filteredClients, setFilteredClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({ status: '', search: '' });
    const [selectedClient, setSelectedClient] = useState(null);
    const filterButtonRef = useRef(null);

    useEffect(() => {
        fetchClients();
    }, []);

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/users`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Filter only clients
            const clientUsers = response.data.users.filter(user => user.role === 'client');
            setClients(clientUsers);
            setFilteredClients(clientUsers);
        } catch (error) {
            console.error('Error fetching clients:', error);
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let result = [...clients];

        if (filters.status) {
            result = result.filter(client => {
                const serviceConfig = client.api_services?.[service.id] || {};
                const isEnabled = serviceConfig.enabled || false;
                return filters.status === 'enabled' ? isEnabled : !isEnabled;
            });
        }

        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            result = result.filter(client =>
                client.name.toLowerCase().includes(searchLower) ||
                client.email.toLowerCase().includes(searchLower)
            );
        }

        setFilteredClients(result);
        setShowFilters(false);
    };

    const clearFilters = () => {
        setFilters({ status: '', search: '' });
        setFilteredClients(clients);
    };

    const handleConfigClose = () => {
        setSelectedClient(null);
        fetchClients(); // Refresh to show updated status
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-lg">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                        <button
                            onClick={onBack}
                            className="mr-4 text-gray-500 hover:text-gray-700 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </button>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{service.name} Clients</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                {filteredClients.length} clients found
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <div className="relative">
                            <button
                                ref={filterButtonRef}
                                onClick={() => setShowFilters(!showFilters)}
                                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center"
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707v4.172a1 1 0 01-.464.864l-3.5 2.333A1 1 0 019 20.586V15.414a1 1 0 00-.293-.707L2.293 8.293A1 1 0 012 7.586V4z" />
                                </svg>
                                Filters
                            </button>
                            <FilterDropdown
                                isOpen={showFilters}
                                onClose={() => setShowFilters(false)}
                                filters={filters}
                                setFilters={setFilters}
                                onApplyFilters={applyFilters}
                                buttonRef={filterButtonRef}
                            />
                        </div>
                        {(filters.status || filters.search) && (
                            <button
                                onClick={clearFilters}
                                className="px-4 py-2 text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                                Clear Filters
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pricing (Req/Doc)</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredClients.map((client) => {
                            const serviceConfig = client.api_services?.[service.id] || {};
                            const isEnabled = serviceConfig.enabled || false;

                            return (
                                <tr key={client.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{client.name}</div>
                                        <div className="text-sm text-gray-500">{client.company}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {client.email}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${isEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                            }`}>
                                            {isEnabled ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {isEnabled ? (
                                            <span>₹{serviceConfig.cost_per_request} / ₹{serviceConfig.cost_per_document}</span>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button
                                            onClick={() => setSelectedClient(client)}
                                            className="text-indigo-600 hover:text-indigo-900"
                                        >
                                            Manage
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Config Modal - Sliding Drawer */}
            <Modal
                isOpen={!!selectedClient}
                onClose={() => setSelectedClient(null)}
                size="max-w-4xl"
            >
                {/* Header */}
                <div className="flex-shrink-0 px-6 py-4 bg-white border-b border-gray-200 flex items-center gap-3">
                    <button
                        onClick={() => setSelectedClient(null)}
                        className="p-1.5 -ml-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all focus:outline-none"
                        aria-label="Back"
                        title="Close"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </button>
                    <h3 className="text-lg font-bold text-gray-900">
                        Manage {service.name} for {selectedClient?.name}
                    </h3>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {selectedClient && (
                        <APIServiceConfig
                            userId={selectedClient.id}
                            onClose={handleConfigClose}
                            targetServiceId={service.id}
                        />
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default AdminAPIServiceClients;
